"use client";

import { useEffect } from "react";

declare global {
  interface Navigator {
    modelContext?: {
      registerTool?: (
        definition: {
          name: string;
          description: string;
          inputSchema: Record<string, unknown>;
          execute: (input: Record<string, unknown>) => unknown | Promise<unknown>;
        },
        options?: { signal?: AbortSignal }
      ) => void | Promise<void>;
      provideContext?: (context: Record<string, unknown>, options?: { signal?: AbortSignal }) => void | Promise<void>;
    };
  }
}

const hotelSearchSchema = {
  type: "object",
  properties: {
    destination: {
      type: "string",
      description: "City, hotel, landmark, or destination to search.",
    },
    checkin: {
      type: "string",
      format: "date",
    },
    checkout: {
      type: "string",
      format: "date",
    },
    adults: {
      type: "integer",
      minimum: 1,
      default: 2,
    },
  },
  required: ["destination"],
};

export default function WebMcpProvider() {
  useEffect(() => {
    const modelContext = navigator.modelContext;
    if (!modelContext) {
      return;
    }

    const controller = new AbortController();
    const origin = window.location.origin;

    const tools = [
      {
        name: "search_hotels",
        description: "Open Roave Travel hotel search for a destination and optional travel dates.",
        inputSchema: hotelSearchSchema,
        execute: async (input: Record<string, unknown>) => {
          const params = new URLSearchParams();
          if (typeof input.destination === "string") params.set("q", input.destination);
          if (typeof input.checkin === "string") params.set("checkin", input.checkin);
          if (typeof input.checkout === "string") params.set("checkout", input.checkout);
          if (typeof input.adults === "number") params.set("adults", String(input.adults));
          const url = `${origin}/hotels${params.size ? `?${params.toString()}` : ""}`;
          window.location.assign(url);
          return { url };
        },
      },
      {
        name: "browse_travel_guides",
        description: "Open Roave Travel public travel guides and blog content.",
        inputSchema: {
          type: "object",
          properties: {
            topic: { type: "string" },
          },
        },
        execute: async (input: Record<string, unknown>) => {
          const params = new URLSearchParams();
          if (typeof input.topic === "string") params.set("filter", input.topic);
          const url = `${origin}/blog${params.size ? `?${params.toString()}` : ""}`;
          window.location.assign(url);
          return { url };
        },
      },
      {
        name: "contact_support",
        description: "Open the Roave Travel contact page for customer support.",
        inputSchema: {
          type: "object",
          properties: {},
        },
        execute: async () => {
          const url = `${origin}/contact`;
          window.location.assign(url);
          return { url };
        },
      },
    ];

    if (modelContext.registerTool) {
      tools.forEach((tool) => {
        void modelContext.registerTool?.(tool, { signal: controller.signal });
      });
    }

    if (modelContext.provideContext) {
      void modelContext.provideContext(
        {
          name: "Roave Travel",
          tools: tools.map(({ name, description, inputSchema }) => ({
            name,
            description,
            inputSchema,
          })),
        },
        { signal: controller.signal }
      );
    }

    return () => controller.abort();
  }, []);

  return null;
}
